import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, RefreshCw, ScanLine } from "lucide-react";
import { describeError } from "@/lib/api";
import {
  uploadDocument, createNoteJob, getNote, isNoteDone, recordUpload, findRecentVisit, patientView,
} from "@/lib/noteClaim";
import { PatientMatchCell } from "@/components/claim/PatientMatch";
import { TID } from "@/lib/testIds";

// Single (or one-visit) document → claim intake. Sent exactly once; coding runs on revQ's
// side. After it lands we poll so the patient shows per the match rules and a no-match can be
// fixed right here — the user may still leave and get an alert.
export default function NoteClaimFlow({
  file, visitFiles, npi, clientId, fingerprint, onRecordUpload,
  onBack, onFinish, onSettled, onQueued, onWatch, onBackground,
}) {
  const [stage, setStage] = useState("uploading"); // uploading | landed | error
  const [uploadPct, setUploadPct] = useState(0);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const cancelled = useRef(false);
  const startedFor = useRef(null);

  const fileName = note?.filename || file?.name || (visitFiles?.length ? `${visitFiles.length} documents` : "your document");
  const fail = (msg, retry) => setError({ msg, retry });

  const onCreated = useCallback((created) => {
    setNote(created); setStage("landed");
    onQueued?.({ id: created.id, filename: created.filename || fileName });
    try { onRecordUpload?.(created); } catch { /* ledger best-effort */ }
    recordUpload(fingerprint, { noteId: created.id, filename: created.filename || fileName, clientId, npi });
  }, [fingerprint, clientId, npi, onQueued, onRecordUpload, fileName]);

  const startOne = useCallback(async (f, npiNumber, isRetry) => {
    if (!f) return;
    setError(null); setUploadPct(0); setStage("uploading");
    try {
      let created;
      try { created = await uploadDocument(f, npiNumber, { onProgress: setUploadPct }); }
      catch (err) {
        // Never blind re-send on a retry — adopt a just-created visit if one exists.
        const recent = isRetry ? await findRecentVisit({ npi: npiNumber, filename: f.name, size: f.size, clientId }) : null;
        if (!recent) throw err;
        created = recent;
      }
      if (cancelled.current) return;
      if (!created?.id) throw new Error("Upload didn't return a note to process.");
      onCreated(created);
    } catch (err) { fail(describeError(err), () => startOne(f, npiNumber, true)); setStage("error"); }
  }, [onCreated, clientId]);

  const startVisit = useCallback(async (files, npiNumber, isRetry) => {
    if (!files?.length) return;
    setError(null); setUploadPct(0); setStage("uploading");
    try {
      let created;
      try { created = await createNoteJob(files, npiNumber, { onProgress: setUploadPct }); }
      catch (err) {
        const recent = isRetry ? await findRecentVisit({ npi: npiNumber, filename: files[0]?.name, size: files[0]?.size, clientId }) : null;
        if (!recent) throw err;
        created = recent;
      }
      if (cancelled.current) return;
      if (!created?.id) throw new Error("Upload didn't return a visit to process.");
      onCreated(created);
    } catch (err) { fail(describeError(err), () => startVisit(files, npiNumber, true)); setStage("error"); }
  }, [onCreated, clientId]);

  const startOneRef = useRef(startOne); startOneRef.current = startOne;
  const startVisitRef = useRef(startVisit); startVisitRef.current = startVisit;

  useEffect(() => {
    cancelled.current = false;
    if (visitFiles?.length && startedFor.current !== visitFiles) {
      startedFor.current = visitFiles; startVisitRef.current(visitFiles, npi);
    } else if (file && startedFor.current !== file) {
      startedFor.current = file; startOneRef.current(file, npi);
    }
    return () => { cancelled.current = true; };
  }, [file, visitFiles, npi]);

  // Once landed, poll so the patient/match surfaces here. Stops when done or the patient is known.
  useEffect(() => {
    if (stage !== "landed" || !note?.id) return;
    let alive = true;
    const t = setInterval(async () => {
      const n = await getNote(note.id).catch(() => null);
      if (!alive || !n) return;
      setNote(n);
      if (isNoteDone(n) || n.match_status) clearInterval(t);
    }, 4500);
    return () => { alive = false; clearInterval(t); };
  }, [stage, note?.id]);

  useEffect(() => { onSettled?.(stage === "landed" || stage === "error"); }, [stage, onSettled]);

  const v = note ? patientView(note) : null;

  return (
    <div className="px-6 pt-5 pb-5" data-testid={TID.noteClaimPanel}>
      {stage === "error" && error ? (
        <div data-testid={TID.noteClaimError} className="rounded-[14px] border border-[rgba(255,159,10,.35)] bg-[rgba(255,159,10,.1)] p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-[#c0392b] mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[var(--ink)]">Couldn’t upload this document</div>
              <div className="text-[11.5px] text-[var(--gray2)] mt-0.5 leading-snug">{error.msg}</div>
            </div>
          </div>
          <div className="flex gap-2.5 mt-4">
            {error.retry ? (
              <button onClick={() => { setError(null); error.retry(); }} data-testid={TID.noteClaimRetryBtn} className="btn-ink inline-flex items-center gap-1.5"><RefreshCw size={13} /> Retry</button>
            ) : null}
            <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--gray1)] hover:text-[var(--ink)] px-3 py-2 rounded-[10px] hover:bg-black/[.04] transition">Try another file</button>
          </div>
        </div>
      ) : null}

      {stage === "uploading" ? (
        <div data-testid={TID.noteClaimUploadProgress} className="text-center py-4">
          <div className="text-[15px] font-extrabold text-[var(--ink)]">Sending the document</div>
          <div className="text-[12px] text-[var(--gray2)] mt-1">Coding starts as soon as it lands, and runs whether or not you stay here.</div>
          <div className="mt-5 mx-auto max-w-[380px]">
            <div className="text-[12px] font-semibold text-[var(--ink)] truncate">{fileName}</div>
            <div className="h-[8px] bg-black/[.08] rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-[var(--ink)] rounded-full transition-all duration-300" style={{ width: `${uploadPct}%` }} />
            </div>
            <div className="text-[10.5px] text-[var(--gray2)] mt-1.5 tabular-nums">{uploadPct}%</div>
          </div>
        </div>
      ) : null}

      {stage === "landed" ? (
        <div data-testid="note-claim-landed" className="py-2">
          <div className="text-center">
            <div className="w-11 h-11 rounded-[13px] bg-[var(--ink)] text-white grid place-items-center mx-auto"><ScanLine size={20} /></div>
            <div className="text-[17px] font-extrabold text-[var(--ink)] mt-3">On its way</div>
            <div className="text-[12.5px] text-[var(--gray2)] mt-0.5">{isNoteDone(note) ? "revQ has coded it" : "revQ is coding it now"}</div>
          </div>

          <div className="mt-4 mx-auto max-w-[420px] rounded-[12px] border border-black/[.08] bg-black/[.02] px-4 py-3">
            <div className="text-[12px] font-semibold text-[var(--ink)] truncate">{fileName}</div>
            {v && (note.match_status || note.patient_name || isNoteDone(note)) ? (
              <div className="mt-2">
                <PatientMatchCell note={note} clientId={clientId} onResolved={setNote} />
              </div>
            ) : (
              <div className="text-[11px] text-[var(--gray2)] mt-1.5">Reading the document and matching the patient…</div>
            )}
          </div>

          <div className="mt-4 mx-auto max-w-[420px] rounded-[12px] bg-black/[.03] px-4 py-3 text-[12px] leading-[1.6] text-[var(--ink2)]">
            The document has landed and coding runs on revQ’s side, so closing this does not stop it — you’ll get an alert when the claim is ready.
          </div>

          <div className="mt-5 mx-auto max-w-[420px] flex items-center gap-3">
            <span className="text-[11px] text-[var(--gray2)]" style={{ whiteSpace: "nowrap" }}>About a minute · you’ll get an alert</span>
            <span className="flex-1" />
            <button onClick={() => onWatch?.({ id: note.id, filename: fileName })} data-testid="note-claim-watch"
              className="text-[12.5px] font-semibold px-3.5 py-2 rounded-[10px] border border-black/[.14] text-[var(--ink)] hover:bg-black/[.04] flex-none">Watch it code</button>
            <button onClick={() => onBackground?.({ id: note.id, filename: fileName })} data-testid="note-claim-background"
              className="text-[12.5px] font-bold px-4 py-2 rounded-[10px] bg-[var(--ink)] text-white flex-none">Run in the background</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
